import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getWfmAccess } from '../services/wfm-service';
import { SHOW_DELIVERY_TEAMS } from '../config/feature-flags';
import useAuthStore from '../stores/auth-store';
import {
  STALE_LONG_MS, clearWfmDenied, isMissingError, retryUnlessMissingUpTo, wfmKeys,
} from './wfm-query';

const retryAccess = retryUnlessMissingUpTo(2);

export function resolveWfmAccess({ enabled, status, data, error }) {
  if (!enabled) return { canAccess: false, isResolved: true, isError: false };
  if (status === 'success' || data !== undefined) return { canAccess: data?.is_in_wfm === true, isResolved: true, isError: false };
  if (status === 'error') {
    const denied = isMissingError(error);
    return { canAccess: false, isResolved: denied, isError: !denied };
  }
  return { canAccess: false, isResolved: false, isError: false };
}

export default function useWfmAccess() {
  const userEmail = useAuthStore((s) => s.userEmail);
  const query = useQuery({
    queryKey: wfmKeys.access(userEmail),
    queryFn: getWfmAccess,
    enabled: SHOW_DELIVERY_TEAMS && !!userEmail,
    staleTime: STALE_LONG_MS,
    retry: retryAccess,
  });
  const verdict = resolveWfmAccess({
    enabled: SHOW_DELIVERY_TEAMS, status: query.status, data: query.data, error: query.error,
  });
  useEffect(() => {
    if (verdict.canAccess) clearWfmDenied();
  }, [verdict.canAccess]);
  return { ...verdict, refetch: query.refetch };
}

export function useWfmForbidden(error) {
  const client = useQueryClient();
  const [latched, setLatched] = useState(false);
  const forbidden = latched || error?.status === 403;
  useEffect(() => {
    if (!forbidden) return;
    setLatched(true);
    client.cancelQueries({ queryKey: ['wfm'] });
    client.removeQueries({ queryKey: ['wfm'], predicate: (q) => q.queryKey[1] !== 'access' });
    client.setQueriesData({ queryKey: ['wfm', 'access'] }, { is_in_wfm: false });
  }, [forbidden, client]);
  return forbidden;
}
